import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useSortableTable } from "../../../../../hooks/sorting/useSortableTable";
import { useColumnResize, ResizeHandle } from "../../../../../components/common/ResizeColumns";
import ConfirmEndDialog from "../../../../../components/common/ConfirmationDialog/ConfirmEndDialog";
import ConfirmDeleteDialog from "../../../../../components/common/ConfirmationDialog/ConfirmDeleteDialog";
import { endAllocation, deleteAllocationRequest } from "../../../../../api/macLeader";

const DELETABLE_STATUSES = ["SUBMITTED", "PMO_APPROVED", "MOVE_PENDING", "REJECTED", "ACTIVE"];
const EDITABLE_STATUSES  = ["ACTIVE", "SUBMITTED", "PMO_APPROVED", "ENDED", "MOVE_PENDING"];

const getInitials = (name = "") => {
  const p = name.trim().split(" ").filter(Boolean);
  if (p.length === 0) return "??";
  if (p.length === 1) return p[0].slice(0, 2).toUpperCase();
  return (p[0][0] + p[p.length - 1][0]).toUpperCase();
};

const AVATAR_COLORS = ["#28B3B4", "#D6DF27", "#4a9960", "#7a5abd", "#c88a3a"];
const avatarColor = (name = "") =>
  AVATAR_COLORS[
    ((name.charCodeAt(0) || 0) + (name.charCodeAt(1) || 0)) %
      AVATAR_COLORS.length
  ];

const DEFAULT_COL_WIDTHS = [200, 70, 150, 150, 110, 130, 70, 80, 100, 140];
const DEFAULT_COL_WIDTHS_WITH_MAC = [200, 70, 150, 150, 110, 130, 130, 70, 80, 100, 140];


// ── ColHeader now accepts onClick + active for sorting ──────────────────────
const ColHeader = ({ label, sortable, active, onClick, t }) => (
  <Typography
    onClick={sortable ? onClick : undefined}
    sx={{
      fontFamily: "'DM Mono', monospace",
      fontSize: "9px",
      letterSpacing: "0.14em",
      textTransform: "uppercase",
      color: active ? t.text2 : t.text3,
      display: "flex",
      alignItems: "center",
      gap: "4px",
      userSelect: "none",
      cursor: sortable ? "pointer" : "default",
    }}
  >
    {label}
  </Typography>
);

const SkeletonRow = ({ t, cols }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: cols,
      padding: "13px 20px",
      borderBottom: `1px solid ${t.border}`,
      alignItems: "center",
      gap: "8px",
    }}
  >
    {[140, 60, 120, 120, 90, 100, 50, 60, 120, 80, 100].map((w, i) => (
      <Box
        key={i}
        sx={{
          height: "11px",
          borderRadius: "3px",
          width: `${w}px`,
          maxWidth: "100%",
          background: t.surface3,
          opacity: 0.6,
        }}
      />
    ))}
  </Box>
);

const EmptyState = ({ t }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flex: 1,
      padding: "60px 24px",
    }}
  >
    <Typography
      sx={{
        fontFamily: "'DM Mono', monospace",
        fontSize: "12px",
        color: t.text3,
        letterSpacing: "0.08em",
      }}
    >
      No allocations found
    </Typography>
  </Box>
);

const STATUS_MAP = {
  ACTIVE:       { label: "· Active",     bg: "rgba(40,179,180,0.12)",  color: "#28B3B4" },
  SUBMITTED:    { label: "· At PMO",     bg: "rgba(214,223,39,0.10)",  color: "#9EA41B" },
  MOVE_PENDING: { label: "· Move Pending", bg: "rgba(200,138,58,0.12)", color: "#c88a3a" },
  PMO_APPROVED: { label: "· At SCG",     bg: "rgba(122,90,189,0.12)",  color: "#7a5abd" },
  REJECTED:     { label: "· Rejected",   bg: "rgba(200,64,64,0.10)",   color: "#c84040" },
  ENDED:        { label: "· Ended",      bg: "rgba(120,120,120,0.10)", color: "#7A9E9C" },
  SUPERSEDED:   { label: "· Superseded", bg: "rgba(120,90,189,0.10)",  color: "#7a5abd" },
};

const StatusBadge = ({ status, t }) => {
  const cfg = STATUS_MAP[status] || { label: `· ${status}`, bg: t.surface3, color: t.text3 };
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", background: cfg.bg, borderRadius: "3px", padding: "3px 8px", maxWidth: "fit-content" }}>
      <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "9px", letterSpacing: "0.1em", color: cfg.color, fontWeight: 600 }}>
        {cfg.label}
      </Typography>
    </Box>
  );
};

const ActionButton = ({ label, onClick, variant = "default", disabled = false, t }) => {
  const styles = {
    default: { border: `1px solid ${t.border2}`, color: t.text2, background: "transparent", hover: { borderColor: t.border3, color: t.text } },
    danger:  { border: "1px solid rgba(200,64,64,0.25)", color: "#c84040", background: "rgba(200,64,64,0.08)", hover: { background: "rgba(200,64,64,0.15)" } },
  };
  const s = styles[variant] || styles.default;
  return (
    <Box onClick={disabled ? undefined : onClick} sx={{ padding: "3px 10px", borderRadius: "2px", cursor: disabled ? "not-allowed" : "pointer", border: s.border, color: s.color, background: s.background, fontFamily: "'DM Mono', monospace", fontSize: "10px", transition: "all 0.15s", opacity: disabled ? 0.35 : 1, "&:hover": disabled ? {} : s.hover }}>
      {label}
    </Box>
  );
};

const AllocationsTable = ({ rows, loading, onView, onEdit, onEnded, onExceptions, showMacColumn = false, showDelete = false, onDeleted }) => {
  const theme = useTheme();
  const t = theme.custom;

  const { buildGrid, getHandleProps } = useColumnResize(
    showMacColumn ? DEFAULT_COL_WIDTHS_WITH_MAC : DEFAULT_COL_WIDTHS
  );

  const [confirmRow, setConfirmRow]       = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmError, setConfirmError]   = useState(null);

  const [deleteRow, setDeleteRow]         = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError]     = useState(null);

  // ── Sorting ────────────────────────────────────────────────────────────────
  const { sortedRows, sortKey, sortDir, handleSort } = useSortableTable(rows ?? [], {
    defaultKey: "associateName",
    defaultDir: "asc",
    comparators: {
      fte:       (a, b) => (a.fte       ?? 0) - (b.fte       ?? 0),
      levelCode: (a, b) => String(a.associateLevelCode ?? a.levelCode ?? "")
                             .localeCompare(String(b.associateLevelCode ?? b.levelCode ?? "")),
      status:    (a, b) => String(a.status ?? "").localeCompare(String(b.status ?? "")),
    },
  });

  // Returns the sort indicator for a given column key
  const ind = (key) => {
    if (sortKey !== key) return " ⇅";
    return sortDir === "asc" ? " ↑" : " ↓";
  };

  // ── Confirm-end handlers ───────────────────────────────────────────────────
  const handleRemoveClick = (row) => { setConfirmRow(row); setConfirmError(null); };
  const handleCancel      = ()    => { if (confirmLoading) return; setConfirmRow(null); setConfirmError(null); };
  const handleConfirm     = async () => {
    try {
      setConfirmLoading(true);
      setConfirmError(null);
      await endAllocation(confirmRow.requestId);
      setConfirmRow(null);
      onEnded?.();
    } catch (err) {
      setConfirmError(err?.message || "Something went wrong. Please try again.");
    } finally {
      setConfirmLoading(false);
    }
  };

  // ── Confirm-delete handlers ────────────────────────────────────────────────
  const handleDeleteClick = (row) => { setDeleteRow(row); setDeleteError(null); };
  const handleDeleteCancel = ()    => { if (deleteLoading) return; setDeleteRow(null); setDeleteError(null); };
  const handleDeleteConfirm = async () => {
    try {
      setDeleteLoading(true);
      setDeleteError(null);
      await deleteAllocationRequest(deleteRow.requestId);
      setDeleteRow(null);
      onDeleted?.();
    } catch (err) {
      setDeleteError(err?.message || "Something went wrong. Please try again.");
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Box sx={{ flex: 1, overflow: "auto", display: "flex", flexDirection: "column" }}>

      {/* ── Sticky header ── */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: buildGrid(),
          padding: "10px 20px",
          borderBottom: `1px solid ${t.border2}`,
          position: "sticky",
          top: 0,
          background: t.surface,
          zIndex: 1,
          gap: "8px",
        }}
      >
        {[
          { label: `Associate${ind("associateName")}`, sk: "associateName" },
          { label: `Level${ind("levelCode")}`,         sk: "levelCode"     },
          { label: "Position ID",                      sk: null            },
          { label: "Role",                             sk: null            },
          ...(showMacColumn ? [{ label: `MAC${ind("macName")}`, sk: "macName" }] : []),
          { label: "Billed",             sk: null     },
          { label: `SOW${ind("sowName")}`,             sk: "sowName"       },
          { label: `Account${ind("accountName")}`,     sk: "accountName"   },
          { label: `FTE${ind("fte")}`,                 sk: "fte"           },
          { label: `Status${ind("status")}`,           sk: "status"        },
          { label: "Actions",                          sk: null            },
        ].map(({ label, sk }, colIndex) => (
  <Box
    key={colIndex}
            onClick={sk ? () => handleSort(sk) : undefined}
            sx={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              cursor: sk ? "pointer" : "default",
            }}
          >
            <ColHeader
              label={label}
              sortable={!!sk}
              active={sk ? sortKey === sk : false}
              t={t}
            />
            <ResizeHandle handleProps={getHandleProps(colIndex)} />
          </Box>
        ))}
      </Box>

      {/* ── Skeleton / empty / data ── */}
      {loading && [1, 2, 3, 4, 5].map((i) => <SkeletonRow key={i} t={t} cols={buildGrid()} />)}
      {!loading && sortedRows.length === 0 && <EmptyState t={t} />}

      {!loading &&
        sortedRows.map((row, i) => {
          const color    = avatarColor(row.associateName);
          const initials = getInitials(row.associateName);
          const status   = row.status;

          // Bench rows are filtered out server-side before they ever reach this
          // table, but none of these actions are valid on one, so guard here too
          // rather than depending on that filter staying in place.
          const isBench        = !!row.isBench;
          const showEdit       = !isBench && EDITABLE_STATUSES.includes(status);
          const showExceptions = !isBench && EDITABLE_STATUSES.includes(status);
          const showEnd        = status === "ACTIVE";
          const showView       = true;
          const canDelete      = showDelete && !isBench && DELETABLE_STATUSES.includes(status);

          return (
            <Box
              key={row.requestId || i}
              sx={{
                display: "grid",
              gridTemplateColumns: buildGrid(),
              padding: "10px 20px",
              gap: "8px",
              borderBottom: i < sortedRows.length - 1 ? `1px solid ${t.border}` : "none",
              alignItems: "center",
              transition: "background 0.15s",
              "&:hover": { background: t.surface2 },
                minWidth: "1180px",
              }}
            >
              {/* Associate — avatar + name */}
              <Box sx={{ display: "flex", alignItems: "center", gap: "9px", minWidth: 0 }}>
                <Box sx={{ width: "28px", height: "28px", borderRadius: "50%", flexShrink: 0, background: `${color}22`, border: `1px solid ${color}55`, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Mono', monospace", fontSize: "9px", fontWeight: 700, color }}>
                  {initials}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "12.5px", fontWeight: 600, color: t.text, lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {row.associateName}
                  </Typography>
                  {row.ownershipType && (
                    <Box sx={{ display: "inline-flex", mt: "2px", background: row.ownershipType === "OWNER" ? t.accent2 : "rgba(214,223,39,0.12)", borderRadius: "2px", padding: "0px 5px" }}>
                      <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "7.5px", letterSpacing: "0.08em", fontWeight: 700, color: row.ownershipType === "OWNER" ? t.accent : t.gold }}>
                        {row.ownershipType}
                      </Typography>
                    </Box>
                  )}
                  {row.isContractor && (
                    <Box sx={{ display: "inline-flex", mt: "2px", padding: "0px 5px", borderRadius: "2px", background: "rgba(214,223,39,0.12)", border: "1px solid rgba(214,223,39,0.35)" }}>
                      <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "7.5px", letterSpacing: "0.06em", fontWeight: 700, color: t.gold }}>
                        CONTRACTOR
                      </Typography>
                    </Box>
                  )}
                  {row.associateDepartment?.toUpperCase() === "TAC" && (
                    <Box sx={{ display: "inline-flex", mt: "2px", padding: "0px 5px", borderRadius: "2px", background: "rgba(100,140,200,0.12)", border: "1px solid rgba(100,140,200,0.30)" }}>
                      <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "7.5px", letterSpacing: "0.06em", fontWeight: 700, color: "#7ba4d4" }}>
                        TAC
                      </Typography>
                    </Box>
                  )}
                  {(row.associateDepartment?.toUpperCase() === "BT" || row.associateDepartment?.toUpperCase() === "TSG") && (
                    <Box sx={{ display: "inline-flex", mt: "2px", padding: "0px 5px", borderRadius: "2px", background: "rgba(150,120,200,0.12)", border: "1px solid rgba(150,120,200,0.30)" }}>
                      <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "7.5px", letterSpacing: "0.06em", fontWeight: 700, color: "#a98fcc" }}>
                        BT
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Level */}
              <Box>
                <Box sx={{ background: t.surface3, borderRadius: "2px", padding: "2px 6px", display: "inline-flex", mb: "2px" }}>
                  <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: t.text2 }}>
                    {row.associateLevelCode || row.levelCode || "—"}
                  </Typography>
                </Box>
                {row.levelTitle && (
                  <Typography sx={{ fontSize: "9px", color: t.text3, lineHeight: 1.3 }}>
                    {row.levelTitle}
                  </Typography>
                )}
              </Box>

              {/* Position ID */}
              <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "10.5px", color: t.accent, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {row.positionCode || "—"}
              </Typography>

              {/* Role */}
              <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "12.5px", fontWeight: 500, color: t.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {row.roleTitle || "—"}
              </Typography>

              {/* MAC — only shown in PMO's cross-MAC "All Allocations" view */}
              {showMacColumn && (
                <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "12px", color: t.text2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {row.macName || "—"}
                </Typography>
              )}

               {/* Billed FTE bar */}
<Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
  {row.billedFte != null ? (
    <>
      <Box sx={{ width: "36px", height: "4px", background: t.surface3, borderRadius: "2px", overflow: "hidden" }}>
        <Box sx={{
          height: "100%",
          width: `${Math.min((row.billedFte ?? 0) * 100, 100)}%`,
          background: (row.billedFte ?? 0) >= 1 ? t.accent : t.gold,
          borderRadius: "2px",
        }} />
      </Box>
      <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "11px", fontWeight: 600, color: t.text2 }}>
        {row.billedFte}
      </Typography>
    </>
  ) : (
    <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "11px", color: t.text3 }}>
      —
    </Typography>
  )}
</Box>

              {/* SOW */}
              <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "12px", color: t.text2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {row.sowName || "—"}
              </Typography>

              {/* Account */}
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "12px", fontWeight: 600, color: t.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", lineHeight: 1.2 }}>
                  {row.accountName || "—"}
                </Typography>
                {row.accountCode && (
                  <Box sx={{ display: "inline-flex", mt: "2px", background: t.surface3, borderRadius: "2px", padding: "0px 5px" }}>
                    <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "8px", letterSpacing: "0.1em", fontWeight: 700, color: t.text3 }}>
                      {row.accountCode}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* FTE bar */}
              <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <Box sx={{ width: "36px", height: "4px", background: t.surface3, borderRadius: "2px", overflow: "hidden" }}>
                  <Box sx={{ height: "100%", width: `${Math.min((row.fte ?? 0) * 100, 100)}%`, background: (row.fte ?? 0) >= 1 ? t.accent : t.gold, borderRadius: "2px" }} />
                </Box>
                <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "11px", fontWeight: 600, color: t.text2 }}>
                  {row.fte ?? "—"}
                </Typography>
              </Box>

             

              {/* Status */}
              <StatusBadge status={row.status} t={t} />

              {/* Actions */}
              <Box sx={{ display: "flex", gap: "5px", alignItems: "center" }}>
                {showView       && <ActionButton label="View"       onClick={() => onView?.(row)}       t={t} />}
                {showEdit       && <ActionButton label="Edit"       onClick={() => onEdit?.(row)}       t={t} />}
                {showExceptions && <ActionButton label="Exceptions" onClick={() => onExceptions?.(row)} disabled={row.positionType === "UBL"} t={t} />}
                {/* {showEnd  && <ActionButton label="End"  onClick={() => handleRemoveClick(row)} variant="danger" t={t} />} */}
                {canDelete && <ActionButton label="Delete" onClick={() => handleDeleteClick(row)} variant="danger" t={t} />}
              </Box>
            </Box>
          );
        })}

      <ConfirmEndDialog
        open={!!confirmRow}
        loading={confirmLoading}
        error={confirmError}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        title="End Allocation"
        confirmLabel="End Allocation"
        warningText="Are you sure you want to end this allocation? The associate will be automatically returned to SCG Pool."
        primaryText={confirmRow?.associateName}
        badgeText={confirmRow?.positionCode}
        subText={`${confirmRow?.roleTitle || "—"} · ${confirmRow?.sowName || "—"}`}
      />

      <ConfirmDeleteDialog
        open={!!deleteRow}
        loading={deleteLoading}
        error={deleteError}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        title="Delete Allocation"
        subtitle={deleteRow?.status === "ACTIVE" ? "This action cannot be undone" : "This permanently removes the request — it cannot be undone"}
        confirmLabel={deleteRow?.status === "ACTIVE" ? "End & Delete" : "Delete Permanently"}
        warningText={
          deleteRow?.status === "ACTIVE"
            ? "This will end the allocation, free the position, and return the associate to " +
              "SCG Pool if they have no other active allocation. This cannot be undone."
            : "This will permanently delete this allocation request and all related " +
              "notifications and approval history. This cannot be undone."
        }
        primaryText={deleteRow?.associateName}
        badgeText={deleteRow?.positionCode}
        subText={`${deleteRow?.roleTitle || "—"} · ${deleteRow?.sowName || "—"}`}
      />
    </Box>
  );
};

export default AllocationsTable;